import { useMemo, useState } from 'react';
import {
  Card,
  Form,
  Radio,
  Button,
  Alert,
  List,
  Popconfirm,
  App,
  Space,
  Typography,
} from 'antd';
import { NumericInput } from '../../components/common';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAddOnPurchase, calculateAddOnPrice, normalizeAddOnQuantity } from '../../hooks/useAddOnPurchase';
import type { AddOnType } from '../../../../shared/config/subscription-addons';
import { getAddOnConfig, calculatePriceWithTax } from '@shared/config/subscription-addons';
import { AddonAddConfirmDialog } from '../../components/company/AddonAddConfirmDialog';
import type { AddonPreview } from '../../components/company/AddonAddConfirmDialog';

const { Title, Text } = Typography;

type SubscriptionAddOnListItem = {
  type: AddOnType;
  quantity: number;
  totalPrice: number;
  startDate: string;
  unitPrice?: number;
  endDate?: string;
};

type SelectableAddOnType = Exclude<AddOnType, 'prediction'>;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : '処理に失敗しました';

/**
 * 追加オプション購入ページ
 */
export function AddonPurchasePage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { data: addOnsData } = trpc.subscriptionAddon.listAddOns.useQuery();
  const { addAddOn, removeAddOn, isAddingAddOn, isRemovingAddOn } = useAddOnPurchase();

  const [selectedType, setSelectedType] = useState<SelectableAddOnType>('equipment');
  const [quantity, setQuantity] = useState<number>(50);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<{ type: SelectableAddOnType; quantity: number } | null>(null);

  // プレビュー取得
  const { data: previewData } = trpc.subscriptionAddon.previewAddOn.useQuery(
    {
      type: pendingValues?.type || 'equipment',
      quantity: pendingValues?.quantity || 50,
    },
    {
      enabled: confirmDialogOpen && !!pendingValues,
    }
  );

  const handleSubmit = async (values: { type: SelectableAddOnType; quantity: number }) => {
    // プレビューを表示するために値を保持
    setPendingValues(values);
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingValues) return;

    try {
      await addAddOn(
        {
          type: pendingValues.type,
          quantity: pendingValues.quantity,
        },
        {
          onSuccess: () => {
            message.success('追加オプションを追加しました');
            form.resetFields();
            setQuantity(50);
            setConfirmDialogOpen(false);
            setPendingValues(null);
          },
          onError: (error) => {
            message.error(`追加オプションの追加に失敗しました: ${error.message}`);
            setConfirmDialogOpen(false);
            setPendingValues(null);
          },
        }
      );
    } catch (error) {
      message.error(`エラーが発生しました: ${getErrorMessage(error)}`);
      setConfirmDialogOpen(false);
      setPendingValues(null);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setPendingValues(null);
  };

  const handleRemoveAddOn = async (index: number) => {
    try {
      await removeAddOn(
        { index },
        {
          onSuccess: () => {
            message.success('追加オプションを解約しました');
          },
          onError: (error) => {
            message.error(`追加オプションの解約に失敗しました: ${error.message}`);
          },
        }
      );
    } catch (error) {
      message.error(`エラーが発生しました: ${getErrorMessage(error)}`);
    }
  };

  const addonConfig = getAddOnConfig(selectedType);
  const unit = addonConfig.unit;
  const unitPrice = addonConfig.unitPrice;
  const normalizedQuantity = normalizeAddOnQuantity(selectedType, quantity);
  const totalPrice = calculateAddOnPrice(selectedType, quantity);
  const totalPriceWithTax = calculatePriceWithTax(totalPrice);
  const addOnList = useMemo<SubscriptionAddOnListItem[]>(() => {
    if (!Array.isArray(addOnsData?.addOns)) {
      return [];
    }
    return addOnsData.addOns
      .filter(
        (item) =>
          Boolean(item?.type && item?.quantity !== undefined && item?.totalPrice !== undefined && item?.startDate)
      )
      .map((item) => ({
        ...item,
        startDate:
          typeof item.startDate === 'string' ? item.startDate : new Date(item.startDate).toISOString(),
        endDate: typeof item.endDate === 'string' ? item.endDate : undefined,
      })) as SubscriptionAddOnListItem[];
  }, [addOnsData]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button onClick={() => navigate('/subscription')} style={{ marginBottom: 16 }}>
            ← サブスクリプション管理に戻る
          </Button>
          <Title level={2}>追加オプション購入</Title>
        </div>

        {/* 追加オプション購入フォーム */}
        <Card title="新しい追加オプションを追加">
          <Alert
            type="warning"
            message="追加オプションについて"
            description={
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>【ご利用について】</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>購入後、即座に上限が追加されます</li>
                    <li>日割り計算で初月分が請求されます</li>
                  </ul>
                </div>
                <div>
                  <strong>【解約について】</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>解約時は機能が即座に停止します</li>
                    <li>解約には使用量を上限以下に減らす必要があります</li>
                    <li>解約時の返金はありません</li>
                  </ul>
                </div>
              </div>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Alert
            type="info"
            message="ストレージ容量は50GB単位に変更されました"
            description="2025年より、ストレージ追加は50GB単位（5,000円/月）でご利用いただけます。"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ type: 'equipment', quantity: 50 }}
          >
            {/* 追加オプションタイプ選択 */}
            <Form.Item
              name="type"
              label="追加オプションタイプ"
              rules={[{ required: true, message: '追加オプションタイプを選択してください' }]}
            >
              <Radio.Group onChange={(e) => {
                const newType = e.target.value as SelectableAddOnType;
                setSelectedType(newType);
                const newConfig = getAddOnConfig(newType);
                setQuantity(newConfig.unit);
                form.setFieldValue('quantity', newConfig.unit);
              }}>
                <Space direction="vertical">
                  {(['equipment', 'storage'] as SelectableAddOnType[]).map((type) => {
                    const config = getAddOnConfig(type);
                    const priceWithTax = calculatePriceWithTax(config.unitPrice);
                    return (
                      <Radio key={type} value={type}>
                        <strong>{config.label}</strong> ({config.unit}{config.unitLabel}単位: ¥{config.unitPrice.toLocaleString()}/月{' '}
                        <Text type="secondary" style={{ color: '#999' }}>(税込{priceWithTax.toLocaleString()}円/月)</Text>)
                      </Radio>
                    );
                  })}
                </Space>
              </Radio.Group>
            </Form.Item>

            {/* 数量入力 */}
            <Form.Item
              name="quantity"
              label={`追加数量 (${unit}${addonConfig.unitLabel}単位)`}
              rules={[
                { required: true, message: '数量を入力してください' },
                { type: 'number', min: unit, message: `最小${unit}${addonConfig.unitLabel}から追加できます` },
              ]}
            >
              <NumericInput
                min={unit}
                step={unit}
                style={{ width: 200 }}
                addonAfter={addonConfig.unitLabel}
                onChange={(val) => {
                  const next = typeof val === 'number' ? val : val ? Number(val) : NaN;
                  setQuantity(Number.isFinite(next) ? next : unit);
                }}
              />
            </Form.Item>

            {/* 料金表示 */}
            <Alert
              type="info"
              message={
                <div>
                  <div>
                    <strong>追加数量:</strong> {normalizedQuantity}{addonConfig.unitLabel}
                    （{Math.ceil(quantity / unit)}ユニット）
                  </div>
                  <div style={{ marginTop: 8, fontSize: 16 }}>
                    <strong>月額料金:</strong>{' '}
                    <span style={{ fontSize: 20 }}>
                      ¥{totalPrice.toLocaleString()}/月
                    </span>
                    {' '}
                    <Text type="secondary" style={{ color: '#999' }}>
                      (税込{totalPriceWithTax.toLocaleString()}円/月)
                    </Text>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                    {Math.ceil(quantity / unit)}ユニット × ¥{unitPrice.toLocaleString()}
                  </div>
                </div>
              }
              style={{ marginBottom: 16 }}
            />

            {/* 送信ボタン */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={isAddingAddOn}
              >
                追加オプションを追加
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* 現在の追加オプション一覧 */}
        <Card title="現在の追加オプション">
          {addOnList.length === 0 ? (
            <Text type="secondary">追加オプションはありません</Text>
          ) : (
            <List<SubscriptionAddOnListItem>
              dataSource={addOnList}
              renderItem={(addOn, index) => (
                <List.Item
                  actions={[
                    <Popconfirm
                      key="delete"
                      title="この追加オプションを解約しますか?"
                      description={
                        <div style={{ maxWidth: 300 }}>
                          <Text type="danger" strong>解約すると即座に機能が停止します。</Text>
                          <br />
                          <Text type="secondary">解約後の返金はありません。</Text>
                        </div>
                      }
                      onConfirm={() => handleRemoveAddOn(index)}
                      okText="解約する"
                      cancelText="キャンセル"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        loading={isRemovingAddOn}
                      >
                        解約
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      addOn.type === 'equipment'
                        ? `設備数追加: ${addOn.quantity}台`
                        : `ストレージ追加: ${addOn.quantity}GB`
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <div>
                          <Text strong style={{ fontSize: 14 }}>
                            ¥{addOn.totalPrice.toLocaleString()}/月{' '}
                          </Text>
                          <Text type="secondary" style={{ color: '#999' }}>
                            (税込{calculatePriceWithTax(addOn.totalPrice).toLocaleString()}円/月)
                          </Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          開始日: {new Date(addOn.startDate).toLocaleDateString('ja-JP')}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* 確認ダイアログ */}
        <AddonAddConfirmDialog
          open={confirmDialogOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          preview={previewData && previewData.addonType ? (previewData as AddonPreview) : null}
          loading={isAddingAddOn}
        />
      </Space>
    </div>
  );
}

export default AddonPurchasePage;
